import {VoiceInputMode, WebRTCServerSource} from 'enums';
import {PreSessionFlowState, SessionActivityTextChatInteractiveChatState, TextChatMessage} from 'types';

export type SessionActivityCoBrowsingData = {
    link: string;
}

export type WebRTCActivityData = {
    server: {
        source: WebRTCServerSource;
        roomName: string
    }
}

export enum ScreenSharingActivityStatus {
    Requested = 'requested',
    Accepted = 'accepted',
    Rejected = 'rejected'
}

export type SessionActivityScreenSharingData = WebRTCActivityData & {
    status: ScreenSharingActivityStatus;
    presenterId: string;
};

export enum AudioStreamState {
    Ready = 'ready',
    Active = 'active',
    Failed = 'failed'
}

export type ConversationSummaryData = {
    summary: string;
}

export type ConversationSummaryActivityData = {
    conversationSummary?: ConversationSummaryData;
}

export type WebRTCSpeechActivityData = WebRTCActivityData & {
    streamAudio: {
        enabled: boolean;
        state?: AudioStreamState
    },
    transcription: {
        [participantId: string]: TextChatMessage[];
    }
    translationSettings: {
        [participantId: string]: {
            language: string;
        }
    }
}

export type SessionActivityAudioVideoData = WebRTCSpeechActivityData & ConversationSummaryActivityData & {
    participants: {
        [participantId: string]: {
            connection: {
                status: string,
                code: string,
                attempt: number,
                message: string
            },
            devicePermission: {
                changedBy: string,
                audio: {
                    disabled: boolean
                },
                video: {
                    disabled: boolean
                }
            }
        };
    }
};

export type SessionActivityTextChatDataState = {
    typing: boolean;
}

export type SessionActivityTranslation = {
    [language: string]: {
        [participantId: string]: {
            [id: string]: {
                content: string;
            }
        }
    }
}

export type SessionActivityTextChatData = ConversationSummaryActivityData & {
    threads: {
        [participantId: string]: TextChatMessage[];
    };
    prevActivitiesData: {
        [activityId: string]: SessionActivityTextChatData;
    },
    state: {
        [participantId: string]: SessionActivityTextChatDataState
    },
    interactiveChatState: SessionActivityTextChatInteractiveChatState;

    translationSettings?: {
        [participantId: string]: {
            language: string;
        }
    },
    translation?: SessionActivityTranslation;
    preSessionFlow?: PreSessionFlowState;
    // Voice input: set when transcription is configured for the text chat. `mode` is pinned at activity start.
    streamAudio?: {
        enabled: boolean;
        mode?: VoiceInputMode;
        // The chatbot answers spoken turns out loud — play the replies that arrive over the socket.
        voiceReply?: boolean;
    };
}

export enum VoiceAgentMode {
    AI = 'ai',
    Human = 'human'
}

export enum VoiceAgentSwitchInitiatedBy {
    Operator = 'operator',
    AIRequest = 'ai-request',
    System = 'system'
}

export enum VoiceAgentProvider {
    InternalLiveAgent = 'internal-live-agent'
}

export type SessionActivityVoiceAgent = {
    provider: VoiceAgentProvider;
    externalSessionId: string;
    currentMode: VoiceAgentMode;
    switches: {
        at: Date;
        from: VoiceAgentMode;
        to: VoiceAgentMode;
        initiatedBy: VoiceAgentSwitchInitiatedBy;
        reason?: string;
    }[];
}

export type SessionActivityAgentChatbotData = {
    threads: {
        [participantId: string]: TextChatMessage[];
    };
}

export type SessionActivitySipData = {
    call?: {
        from: string;
        to: string;
    };
    conference?: {
        name: string;
    };
    voiceAgent?: SessionActivityVoiceAgent;
}

export type SessionActivityData =
    any |
    SessionActivityScreenSharingData |
    SessionActivityAudioVideoData |
    SessionActivityCoBrowsingData |
    SessionActivitySipData |
    SessionActivityTextChatData |
    SessionActivityAgentChatbotData;